import { useDispatch, useSelector } from 'react-redux';
import {
  isValidCard,
  cardExpiryFormat,
  getCVVLength,
  getIcon,
  getMask,
  isValidCvv,
  validateExpiryDate,
} from '../../utils/card';
import makeStyles from './style';
import AmericanExpress from '../../assets/icons/svg/AmericanExpress';
import MasterCard from '../../assets/icons/svg/Mastercard';
import Visa from '../../assets/icons/svg/Visa';
import Mada from '../../assets/icons/svg/Mada';
import { useTranslation } from 'react-i18next';
import NumberFormat from 'react-number-format';
import InformativeModal from '../informativeModal';
import RememberMeSwitch from '../RememberMeSwitch';
import { useForm, Controller } from 'react-hook-form';
import TransactionFailed from '../transactionFaild';
import {
  cardHolderFullNamePattern,
  cardHolderNameCharactersPattern,
} from '../../constants/fromConstants';
import ClearButton from '../clearButton';
import {
  requestCardToken,
  requestPayment,
  storeCard,
  applePaySessionHandel,
  paymentRequestHandel,
} from '../../redux/actions/checkout';
import Loading from '../Loading/Loading';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { calculatePrice } from '../../redux/actions/subscriptionMetadata';
import { getLocalizedNumber } from '../../localization';
import { updateSubscription } from '../../redux/actions/subscriptionMetadata';
import Stack from '@mui/material/Stack';
import DiscountIcon from '../../assets/icons/svg/Discount';
import clsx from 'clsx';
import { mixpanel } from '../../analytics/mixpanel'

const defaultValues = {
  remember_me: true,
  expiry_date: '',
  card_number: '',
  card_holder_name: '',
};

const Form = () => {
  const dispatch = useDispatch();
  const { meta, language, isLoading, subscriptionId, authToken } = useSelector(
    (state) => ({
      meta: state?.metadata?.object,
      authToken: state?.appState?.token,
      language: state?.appState?.language,
      subscriptionId: state?.appState?.subscriptionId,
      isLoading: state?.checkout?.isLoading,
    }),
  );
  const classes = makeStyles({ language });
  const { t } = useTranslation();
  const {
    register,
    formState: { errors, isSubmitted },
    watch,
    trigger,
    setValue,
    handleSubmit,
    control,
  } = useForm({ defaultValues });
  const [cardHolderName, cardNumber, rememberMe, expiryDate, cvv] = watch([
    'card_holder_name',
    'card_number',
    'remember_me',
    'expiry_date',
    'card_security_code',
  ]);
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const [visualHeight, setVisualHeight] = useState(
    window.visualViewport.height,
  );
  const CardLogo = getIcon(cardNumber);
  const inputRefs = useRef({});

  useEffect(() => {
    if (meta?.clientId) {
      mixpanel.identify(meta?.clientId.toString());
    }

    return () => {
      if (meta?.clientId) {
        mixpanel.reset(meta?.clientId.toString())
      }
    };
  }, [meta?.clientId]);

  const onSubmit = (data) => {
    const firstSixCharactersOfTheCardNumber = String(data.card_number).substr(0, 6);
    const bankBin = firstSixCharactersOfTheCardNumber;
    mixpanel.track('pf_pay_now_clicked', {
      subscriptionId,
      clientId: meta?.clientId,
      amount: meta?.amount.toFixed(2).toString(),
    })
    requestCardToken(data)
      .then(async (cardToken) => {
        if (authToken) {
          await dispatch(
            updateSubscription({
              subscriptionId,
              bankBin,
            }),
          );
        }
        return cardToken;
      })
      .then((token) => {
        if (rememberMe) {
          return requestCardToken(data)
            .then((cardToken) => storeCard(cardToken))
            .then(() => requestPayment(token, rememberMe));
        } else {
          return requestPayment(token, rememberMe);
        }
      });
  };

  const recalculatePriceOnBlur = useCallback(() => {
    const firstSixCharactersOfTheCardNumber = String(cardNumber).substr(0, 6);
    const bankBin = firstSixCharactersOfTheCardNumber;

    if ((isValidCard(cardNumber) || cardNumber === '') && authToken) {
      dispatch(calculatePrice({ bankBin }));
    }
  }, [cardNumber, dispatch, authToken]);

  useEffect(() => {
    setTimeout(() => {
      setVisualHeight(window.visualViewport.height);
    }, 1000);
  }, [isKeyboardOpen]);

  const focusOnCurrentClickedInput = (currentInput) => {
    // 1- Blur all inputs
    Object.values(inputRefs.current).forEach((value) => value.blur());

    // 2- Focus on current clicked input
    currentInput.focus();
  };

  const isApplePayAvailable = useMemo(() => {
    return (window.ApplePaySession && window.ApplePaySession.canMakePayments()) || (window.PaymentRequest && !!meta?.merchantIdentifier);
  }, [meta?.merchantIdentifier]);

  const onApplePayButtonClicked = useCallback(async () => {
    mixpanel.track('pf_apple_pay_clicked', {
      subscriptionId,
      clientId: meta?.clientId,
      amount: meta?.amount.toFixed(2).toString(),
    })
    if (isApplePayAvailable) {
      // Consider falling back to Apple Pay JS if Payment Request is not available.
      if (window.PaymentRequest) {
        paymentRequestHandel(subscriptionId, meta);
      } else {
        applePaySessionHandel(subscriptionId, meta);
      }
    }
  }, [isApplePayAvailable, meta, subscriptionId]);

  useEffect(() => {
    if (isApplePayAvailable) {
      const applePayButton = document.querySelector('apple-pay-button');

      if (applePayButton) {
        applePayButton.style.marginTop = '15px';
        applePayButton.addEventListener('click', onApplePayButtonClicked);
      }

      return () => {
        if (applePayButton) {
          applePayButton.removeEventListener('click', onApplePayButtonClicked);
        }
      }
    }
  }, [isApplePayAvailable, onApplePayButtonClicked]);

  const addonPriceJsx = (
    <div className={classes.totalTextContainer}>
      <span className={classes.totalText}>{t('addon')}</span>
      <span className={classes.totalText}>{meta?.prettyAddonPrice}</span>
    </div>
  );

  return (
    <>
      <Loading visible={isLoading} />
      <form
        {...(isMobile && { style: { height: visualHeight } })}
        className={classes.form}
        id={'cardForm'}
        name={'cardForm'}
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className={classes.formContainer}>
          {!isMobile && (
            <div className={classes.formTitleContainer}>
              <h2 className={classes.formTitle}>{t('title')}</h2>
            </div>
          )}
          <div className={classes.fieldsWrapper}>
            <Stack spacing="18px">
              {/* card holder name */}
              <div>
                <h5 className={`${classes.formLabel} ${classes.firstLabel}`}>
                  {t('cardHolderName')}
                </h5>
                <div
                  className={`${classes.formFieldContainer}
                 ${errors?.card_holder_name && classes.fieldError}
                `}
                >
                  <Controller
                    control={control}
                    className={classes.formField}
                    name={'card_holder_name'}
                    rules={{
                      required: t('required'),
                      validate: (value) => {
                        if (
                          !cardHolderNameCharactersPattern.test(value?.trim())
                        ) {
                          return t('cardNameCharactersValidation');
                        }
                        if (!cardHolderFullNamePattern.test(value?.trim())) {
                          return t('cardFullNameValidation');
                        }
                      },
                    }}
                    render={({ field }) => {
                      return (
                        <input
                          name={field.name}
                          onChange={field.onChange}
                          value={cardHolderName}
                          onFocus={() => {
                            setIsKeyboardOpen(true);
                          }}
                          onBlur={() => {
                            inputRefs.current.cardNumber.focus();
                            setIsKeyboardOpen(false);
                          }}
                          ref={(ref) => {
                            inputRefs.current.cardHolderName = ref;
                          }}
                          autoComplete={'cc-name'}
                          type={'text'}
                          placeholder={t('cardHolderNamePlaceholder')}
                          className={classes.formField}
                          onClick={(e) => focusOnCurrentClickedInput(e.target)}
                        />
                      );
                    }}
                  />
                  {cardHolderName && (
                    <ClearButton
                      onClick={() => {
                        setValue('card_holder_name', '');
                        isSubmitted && trigger('card_holder_name');
                        focusOnCurrentClickedInput(
                          inputRefs.current.cardHolderName,
                        );
                      }}
                    />
                  )}
                </div>

                {errors?.card_holder_name?.message ? (
                  <h6 className={classes.errorMessage}>
                    {errors.card_holder_name.message}
                  </h6>
                ) : null}
              </div>
              {/* card number*/}
              <div>
                <h5 className={classes.formLabel}>{t('cardNumber')}</h5>
                <div className={`${classes.cardNumberLogoContainer}`}>
                  <div className={`${classes.cardLogoBackground}`}>
                    <div className={classes.logoTypeWrapper}>
                      {
                        <CardLogo
                          style={{
                            transform: `scale(${
                              CardLogo.name === 'CardNumber' ? 1 : 0.5
                            })`,
                          }}
                        />
                      }
                    </div>
                  </div>
                </div>

                <div
                  className={`${classes.formFieldContainer} ${
                    errors?.card_number && classes.fieldError
                  }`}
                >
                  <Controller
                    control={control}
                    className={`${classes.formField} ${classes.cardNumberField}`}
                    name={'card_number'}
                    rules={{
                      required: t('required'),
                      validate: (value) =>
                        isValidCard(value) || t('card_number_invalid'),
                    }}
                    render={({ field }) => {
                      return (
                        <NumberFormat
                          name={'card_number'}
                          displayType={'input'}
                          type={'text'}
                          placeholder={t('cardNumberPlaceholder')}
                          format={getMask(cardNumber)}
                          autoComplete={'cc-number'}
                          onValueChange={(e) => {
                            field.onChange(e.value);
                          }}
                          value={cardNumber}
                          onFocus={() => {
                            setIsKeyboardOpen(true);
                          }}
                          onBlur={() => {
                            inputRefs.current.expiryDate.focus();
                            recalculatePriceOnBlur();
                            setIsKeyboardOpen(false);
                          }}
                          getInputRef={(ref) =>
                            (inputRefs.current.cardNumber = ref)
                          }
                          onClick={(e) => focusOnCurrentClickedInput(e.target)}
                          className={`${classes.formField} ${classes.cardNumberField}`}
                        />
                      );
                    }}
                  />

                  {cardNumber && (
                    <ClearButton
                      onClick={() => {
                        dispatch(calculatePrice());
                        setValue('card_number', '');
                        focusOnCurrentClickedInput(
                          inputRefs.current.cardNumber,
                        );
                      }}
                    />
                  )}
                </div>
                {errors?.card_number?.message ? (
                  <h6 className={classes.errorMessage}>
                    {errors.card_number.message}
                  </h6>
                ) : null}
              </div>
              {/* expiry date and cvv */}
              <div className={classes.formRow}>
                {/* expiry date */}
                <div className={classes.formSmallRow}>
                  <h5 className={classes.formLabel}>{t('expiryDate')}</h5>
                  <div
                    className={`${classes.formFieldContainer} ${
                      errors?.expiry_date && classes.fieldError
                    }`}
                  >
                    <Controller
                      control={control}
                      className={`${classes.formField} ${classes.cardNumberField}`}
                      name={'expiry_date'}
                      rules={{
                        required: t('required'),
                        validate: validateExpiryDate,
                        pattern: /\d{2}\/\d{2}/,
                      }}
                      render={({ field }) => {
                        return (
                          <NumberFormat
                            name={'expiry_date'}
                            type={'text'}
                            placeholder={'MM/YY'}
                            mask={['M', 'M', 'Y', 'Y']}
                            className={classes.formField}
                            autoComplete={'cc-exp'}
                            format={cardExpiryFormat}
                            onValueChange={(e) => {
                              field.onChange(e.value);
                            }}
                            {...field}
                            onFocus={() => {
                              setIsKeyboardOpen(true);
                            }}
                            onBlur={() => {
                              setIsKeyboardOpen(false);
                              inputRefs.current.cvv.focus();
                            }}
                            getInputRef={(ref) =>
                              (inputRefs.current.expiryDate = ref)
                            }
                            onClick={(e) =>
                              focusOnCurrentClickedInput(e.target)
                            }
                          />
                        );
                      }}
                    />

                    <InformativeModal type={'expiryDate'} />
                  </div>

                  {errors?.expiry_date ? (
                    <h6 className={classes.errorMessage}>
                      {errors?.expiry_date?.message
                        ? errors.expiry_date.message
                        : errors?.expiry_date?.type === 'validate'
                        ? t('expiryDateValidation')
                        : errors?.expiry_date?.type === 'pattern'
                        ? t('invalidDate')
                        : ''}
                    </h6>
                  ) : null}
                </div>

                {/* cvv */}
                <div className={classes.formSmallRow}>
                  <h5 className={classes.formLabel}>{t('cvv')}</h5>

                  <div
                    className={`${classes.formFieldContainer} ${
                      errors?.card_security_code && classes.fieldError
                    }`}
                  >
                    <Controller
                      control={control}
                      className={`${classes.formField} ${classes.cardNumberField}`}
                      name={'card_security_code'}
                      rules={{
                        required: t('required'),
                        validate: (value) =>
                          isValidCvv(value, cardNumber) || t('cvv_invalid'),
                      }}
                      render={({ field }) => {
                        return (
                          <NumberFormat
                            name={'card_security_code'}
                            type={'text'}
                            placeholder={t('cvvPlaceholder')}
                            className={classes.formField}
                            autoComplete={'cc-'}
                            format={getCVVLength(cardNumber)}
                            onValueChange={(e) => {
                              field.onChange(e.value);
                            }}
                            {...field}
                            onFocus={() => {
                              setIsKeyboardOpen(true);
                            }}
                            onBlur={() => {
                              setIsKeyboardOpen(false);

                              if (
                                !(
                                  cardHolderName
                                    ?.toString()
                                    ?.replace(/ /g, '') &&
                                  cardNumber?.toString()?.replace(/ /g, '') &&
                                  expiryDate?.toString()?.replace(/ /g, '') &&
                                  cvv?.toString()?.replace(/ /g, '')
                                )
                              ) {
                                handleSubmit(onSubmit);
                              }
                            }}
                            getInputRef={(ref) => (inputRefs.current.cvv = ref)}
                            onClick={(e) =>
                              focusOnCurrentClickedInput(e.target)
                            }
                          />
                        );
                      }}
                    />
                    <InformativeModal type="cvv" />
                  </div>
                  {errors?.card_security_code?.message ? (
                    <h6 className={classes.errorMessage}>
                      {errors?.card_security_code?.message}
                    </h6>
                  ) : null}
                </div>
              </div>
              {/* remember me */}
              <div className={classes.formRow}>
                <p className={classes.rememberMeText}>{t('rememberMeText')}</p>
                <RememberMeSwitch
                  inputProps={{ 'aria-label': 'controlled' }}
                  refs={register('remember_me')}
                  checked={rememberMe}
                  onChange={(e) => setValue('remember_me', e.target.checked)}
                  sx={{ m: 1 }}
                  language={language}
                />
              </div>
            </Stack>
          </div>
          <div
            {...(!isMobile && {
              style: { position: 'relative', marginTop: '32px' },
            })}
            className={`${isMobile ? classes.submitContainerMobile : ''}`}
          >
            <div className={classes.logosContainer}>
              <AmericanExpress />
              <Visa />
              <MasterCard />
              <Mada />
            </div>
            {meta?.hasPromotedBankDiscount ? (
              <>
                {meta?.subTotal ? (
                  <div className={classes.totalTextContainer}>
                    <span className={classes.totalText}>{t('plan_price')}</span>
                    <span className={classes.totalText}>
                      {meta?.prettySubTotal}
                    </span>
                  </div>
                ) : null}
                {meta?.addonId && meta?.discountAppliedOnAddon === true
                  ? addonPriceJsx
                  : null}
                {meta?.discount ? (
                  <div
                    style={{ color: '#4CAF50' }}
                    className={classes.totalTextContainer}
                  >
                    <span
                      className={clsx(
                        classes.totalText,
                        classes.promotedBankDiscount,
                      )}
                      dir="auto"
                    >
                      {t('promoted_bank_discount', {
                        promotedBankName: meta?.promotedBankName,
                        discountAmount: getLocalizedNumber(
                          meta?.discountAmount,
                        ),
                        type:
                          meta?.voucherType === 'PERCENTAGE'
                            ? '%'
                            : meta?.currency,
                        extraNote: meta?.promotedBankNote,
                      })}
                      <DiscountIcon style={{ marginInlineStart: '6px' }} />
                    </span>
                    <span className={classes.totalText}>
                      {meta?.prettyDiscount}
                    </span>
                  </div>
                ) : null}
                {meta?.programDiscount ? (
                  <div
                    style={{ color: '#4CAF50' }}
                    className={classes.totalTextContainer}
                  >
                    <span
                      className={classes.totalText}
                      style={{ display: 'inline-flex' }}
                      dir="auto"
                    >
                      {t('program_discount')}
                      <DiscountIcon style={{ marginInlineStart: '6px' }} />
                    </span>
                    <span className={classes.totalText}>
                      {meta?.prettyProgramDiscount}
                    </span>
                  </div>
                ) : null}
                {meta?.addonId && meta?.discountAppliedOnAddon === false
                  ? addonPriceJsx
                  : null}
                {meta?.taxes ? (
                  <div className={classes.totalTextContainer}>
                    <span className={classes.totalText}>{t('vat')}</span>
                    <span className={classes.totalText}>
                      {meta?.prettyTaxes}
                    </span>
                  </div>
                ) : null}
                {meta?.mokafaaAmount ? (
                  <div
                    className={classes.totalTextContainer}
                    style={{ color: '#4CAF50' }}
                  >
                    <span className={classes.totalText}>
                      {t('rajhi_mokafaa')}
                    </span>
                    <span className={classes.totalText}>
                      {meta?.prettyMokafaaAmount}
                    </span>
                  </div>
                ) : null}
                {meta?.walletPayment ? (
                  <div
                    className={classes.totalTextContainer}
                    style={{ color: '#4CAF50' }}
                  >
                    <span className={classes.totalText}>{t('wallet')}</span>
                    <span className={classes.totalText}>
                      {meta?.prettyWalletPayment}
                    </span>
                  </div>
                ) : null}
                {meta.priceAfterDisscount >= 0 ? (
                  <div
                    style={{ fontWeight: '700' }}
                    className={classes.totalTextContainer}
                  >
                    <span className={classes.totalText}>
                      {t('total')}
                      <span className={classes.totalSpan}>{t('taxes')}</span>
                    </span>
                    <span className={classes.totalText}>
                      {meta?.prettyPriceAfterDiscount}
                    </span>
                  </div>
                ) : null}
              </>
            ) : (
              <div
                style={{ fontWeight: '700' }}
                className={classes.totalTextContainer}
              >
                <span className={classes.totalText}>
                  {t('total')}
                  <span className={classes.totalSpan}>{t('taxes')}</span>
                </span>
                <span className={classes.totalText}>{meta?.price}</span>
              </div>
            )}
            <button
              className={classes.submitButton}
              type="submit"
              disabled={
                !(
                  cardHolderName?.toString()?.replace(/ /g, '') &&
                  cardNumber?.toString()?.replace(/ /g, '') &&
                  expiryDate?.toString()?.replace(/ /g, '') &&
                  cvv?.toString()?.replace(/ /g, '')
                )
              }
            >
              {t('payNow')}
            </button>
            {isApplePayAvailable ? (
              <apple-pay-button
                buttonstyle="black"
                type="plain"
                locale="en-US"
                lang="en-US"
              ></apple-pay-button>
            ) : null}
          </div>
        </div>
      </form>
      <TransactionFailed />
    </>
  );
};

export default Form;
